/**
 * Der Anlagen-Umschalter am Kopf der Seitenleiste.
 *
 * Übernommen aus `ui_kits/dashboard/Sidebar.jsx`. Er hat zwei Gestalten, und
 * welche gilt, entscheidet **nicht** die Plattform-Rolle unmittelbar, sondern
 * ob es hier überhaupt etwas zu tun gibt: Wer genau eine Anlage hat und keine
 * zweite anlegen darf, bekommt einen ruhigen Kopf ohne Klickfläche — ein
 * Auswahlfeld mit einer einzigen Möglichkeit ist eine Einladung zu einem Klick,
 * der nichts tut. Sonst klappt ein Menü auf.
 *
 * **Das Menü führt die Anlagen und den Weg zu einer weiteren.** Anlagen
 * anzulegen und zu verwalten gehört an die Stelle, an der die Anlage gewählt
 * wird, und nicht ins Kontomenü am Fuß der Leiste: Wer eine zweite Anlage
 * anlegen will, sucht sie dort, wo die erste steht. Das Kontomenü führt seither
 * nur noch, was mit keiner Anlage zu tun hat.
 *
 * **Der Aufbau ist der der Vorlage**, bis auf die Maße genau: die
 * Kapitälchen-Überschrift, das Symbolkästchen von 28 px je Zeile, der
 * Hinterlegungswechsel beim Überfahren, die Trennlinie und darunter der grüne
 * Eintrag „Anlage hinzufügen". Zwei Dinge sind bewusst anders — der Nachbau
 * dort ist mit der Tastatur nicht bedienbar und kennt nur eine Anlagenart:
 *
 * 1. Escape schließt und gibt den Fokus zurück, die Einträge sind echte
 *    Schaltflächen, und die Auswahl steht in `aria-checked` statt nur im Haken.
 * 2. **Das Symbol kommt aus der Art der Anlage** (`lib/site-kind.ts`), nicht
 *    fest aus einem Gebäude-Zeichen. Wer eine Wohnung und ein Gewerbe führt,
 *    unterscheidet sie in der Liste sonst nur am Namen.
 *
 * **Und seit HM-082 stehen hier auch die Wohnungen, in denen der Nutzer wohnt.**
 * Der Umschalter sagt, worauf sich alles darunter bezieht — und für einen
 * Bewohner ist das seine Wohnung, nicht eine Anlage. Sie standen zuerst im
 * Kontomenü am Fuß der Leiste, wo niemand nach dem sucht, was oben gewählt
 * wird. Anlagen und Wohnungen sind zwei Gruppen mit je eigener Überschrift;
 * eine Wohnung trägt das Tür-Symbol, und ihre zweite Zeile nennt die Anlage.
 *
 * **Und seit HM-160 die Anlagen, die mit dem Nutzer geteilt sind** — in derselben
 * Gruppe wie die eigenen, denn gewählt wird hier wie dort eine Anlage. Ihre
 * zweite Zeile sagt statt der Anschrift, wem sie gehört und was man darf.
 */
import { useEffect, useId, useRef, useState } from "react";
import { icons } from "@hausmeter/ui";
import type { ApiComponents } from "@hausmeter/sdk";
import { siteKindIcon } from "../lib/site-kind.js";

type Site = ApiComponents["schemas"]["Site"];
type MyUnit = ApiComponents["schemas"]["MyUnit"];

export interface SiteSwitcherProps {
  readonly sites: readonly Site[];
  readonly activeSiteId: string | null;
  readonly onSelect: (siteId: string) => void;
  /** Die Wohnungen, in denen der Nutzer wohnt (HM-081) — leer, wenn keine. */
  readonly units?: readonly MyUnit[];
  /** Die gewählte Wohnung; gesetzt, steht sie im Kopf statt einer Anlage. */
  readonly activeUnitId?: string | null;
  readonly onSelectUnit?: (siteAreaId: string) => void;
  /**
   * Führt zur Anlagenverwaltung — dort steht das Formular für eine weitere
   * Anlage. Fehlt der Rückruf, darf dieses Konto keine weitere anlegen: Das
   * Kontingent ist ausgeschöpft, und ein Eintrag, der nur zu einem `403` führt,
   * wird ausgeblendet und nicht ausgegraut (Regel 10).
   */
  readonly onAddSite?: () => void;
  /**
   * Die zweite Zeile geteilter Anlagen (HM-160) — „Geteilt von … · Lesen".
   * Eine Anlage, die hier fehlt, gehört dem Nutzer selbst.
   */
  readonly sharedLabels?: ReadonlyMap<string, string>;
}

export function SiteSwitcher({
  sites,
  activeSiteId,
  onSelect,
  units = [],
  activeUnitId = null,
  onSelectUnit,
  onAddSite,
  sharedLabels,
}: SiteSwitcherProps) {
  const detailOf = (site: Site): string => sharedLabels?.get(site.id) ?? secondLine(site);
  const [open, setOpen] = useState(false);
  /** Welche Zeile gerade überfahren wird — die Anlagenkennung oder `"add"`. */
  const [hovered, setHovered] = useState<string | null>(null);
  const container = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const headingId = useId();

  const activeUnit = activeUnitId === null ? undefined : units.find((unit) => unit.siteAreaId === activeUnitId);
  const activeSite = activeUnit !== undefined ? undefined : (sites.find((site) => site.id === activeSiteId) ?? sites[0]);
  /** Was im Kopf steht — eine Anlage oder eine Wohnung, nie beides. */
  const active =
    activeUnit !== undefined
      ? { id: activeUnit.siteAreaId, name: activeUnit.unitName, detail: `${activeUnit.siteName} · Deine Wohnung`, Icon: icons.IconDoor }
      : activeSite !== undefined
        ? { id: activeSite.id, name: activeSite.name, detail: detailOf(activeSite), Icon: siteKindIcon(activeSite.kind) }
        : /* Keine Anlage, aber Wohnungen — dann die erste, wie sonst die erste Anlage. */
          units[0] !== undefined
          ? { id: units[0].siteAreaId, name: units[0].unitName, detail: `${units[0].siteName} · Deine Wohnung`, Icon: icons.IconDoor }
          : undefined;

  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: MouseEvent): void {
      if (container.current && !container.current.contains(event.target as Node)) setOpen(false);
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key !== "Escape") return;
      setOpen(false);
      // Der Fokus muss zurück an den Knopf — sonst steht er nach dem Schließen
      // am Seitenanfang, und die Tastatur beginnt von vorn.
      trigger.current?.focus();
    }

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  if (active === undefined) return null;

  const ActiveIcon = active.Icon;

  const head = (
    <>
      {/* Maße aus `Sidebar.jsx`: 34 px, Radius 9, Marken-Grün auf hellem Grund. */}
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: 9,
          background: "var(--hm-green-50)",
          color: "var(--hm-green-600)",
        }}
      >
        <ActiveIcon size={18} />
      </span>
      <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <span style={{ display: "block", ...NAME_STYLE }}>{active.name}</span>
        <span style={{ display: "block", ...DETAIL_STYLE }}>{active.detail}</span>
      </span>
    </>
  );

  // Ein einziger Eintrag — Anlage oder Wohnung — und keine weitere erlaubt:
  // nur ein Kopf, kein Knopf.
  if (sites.length + units.length < 2 && onAddSite === undefined) {
    return (
      <div style={{ ...SHELL_STYLE, display: "flex", alignItems: "center", gap: 11 }}>{head}</div>
    );
  }

  return (
    <div ref={container} style={{ position: "relative" }}>
      <button
        ref={trigger}
        type="button"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={() => setOpen((previous) => !previous)}
        style={{
          ...SHELL_STYLE,
          display: "flex",
          alignItems: "center",
          gap: 11,
          width: "100%",
          cursor: "pointer",
          borderColor: open ? "var(--border-brand)" : "var(--border-subtle)",
          boxShadow: open ? "0 0 0 var(--ring-width) var(--focus-ring)" : "none",
          transition: "border-color .15s ease, box-shadow .15s ease",
        }}
      >
        {head}
        <icons.IconUnfold size={15} style={{ color: "var(--text-faint)", flexShrink: 0 }} />
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Anlagen"
          style={{
            position: "absolute",
            top: "calc(100% + 6px)",
            left: 0,
            right: 0,
            zIndex: 60,
            padding: 5,
            background: "var(--surface-card)",
            border: "1px solid var(--border-subtle)",
            borderRadius: "var(--radius-md)",
            boxShadow: "var(--shadow-lg)",
          }}
        >
          {/* Die Kapitälchen-Zeile der Vorlage. Sie ist keine bloße Zierde: Sie
              beschriftet die Gruppe darunter und trennt sie damit von dem
              Eintrag, der unter der Linie folgt und nichts auswählt. */}
          {sites.length > 0 && (
          <div id={headingId} style={HEADING_STYLE}>
            Anlage wechseln
          </div>
          )}

          <div role="group" aria-labelledby={headingId}>
            {sites.map((site) => {
              const selected = site.id === active.id;
              const KindIcon = siteKindIcon(site.kind);

              return (
                <button
                  key={site.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={selected}
                  onMouseEnter={() => setHovered(site.id)}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => {
                    onSelect(site.id);
                    setOpen(false);
                    trigger.current?.focus();
                  }}
                  style={{
                    ...ITEM_STYLE,
                    background: selected
                      ? "var(--hm-green-50)"
                      : hovered === site.id
                        ? "var(--surface-sunken)"
                        : "transparent",
                  }}
                >
                  <span
                    style={{
                      ...ITEM_ICON_STYLE,
                      background: selected ? "var(--hm-green-100)" : "var(--surface-sunken)",
                      color: selected ? "var(--hm-green-700)" : "var(--text-muted)",
                    }}
                  >
                    <KindIcon size={15} />
                  </span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span
                      style={{
                        display: "block",
                        ...ITEM_NAME_STYLE,
                        fontWeight: selected ? 600 : 500,
                      }}
                    >
                      {site.name}
                    </span>
                    <span style={{ display: "block", ...ITEM_DETAIL_STYLE }}>
                      {detailOf(site)}
                    </span>
                  </span>
                  {/* Der Haken ist nicht der alleinige Träger: `aria-checked` sagt
                      dasselbe, und die Hinterlegung zeigt es noch einmal. */}
                  {selected && (
                    <icons.IconCheck
                      size={15}
                      style={{ color: "var(--hm-green-600)", flexShrink: 0 }}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {units.length > 0 && (
            <>
              {/* Die Wohnungen als zweite Gruppe — was dem Nutzer gehört, weil
                  er dort wohnt, nicht weil er die Anlage führt (HM-081). */}
              {sites.length > 0 && (
                <div role="separator" style={{ height: 1, background: "var(--border-subtle)", margin: "5px 4px" }} />
              )}
              <div id={`${headingId}-units`} style={HEADING_STYLE}>
                {units.length === 1 ? "Deine Wohnung" : "Deine Wohnungen"}
              </div>
              <div role="group" aria-labelledby={`${headingId}-units`}>
                {units.map((unit) => {
                  const selected = unit.siteAreaId === active.id;
                  return (
                    <button
                      key={unit.siteAreaId}
                      type="button"
                      role="menuitemradio"
                      aria-checked={selected}
                      onMouseEnter={() => setHovered(unit.siteAreaId)}
                      onMouseLeave={() => setHovered(null)}
                      onClick={() => {
                        onSelectUnit?.(unit.siteAreaId);
                        setOpen(false);
                        trigger.current?.focus();
                      }}
                      style={{
                        ...ITEM_STYLE,
                        background: selected
                          ? "var(--hm-green-50)"
                          : hovered === unit.siteAreaId
                            ? "var(--surface-sunken)"
                            : "transparent",
                      }}
                    >
                      <span
                        style={{
                          ...ITEM_ICON_STYLE,
                          background: selected ? "var(--hm-green-100)" : "var(--surface-sunken)",
                          color: selected ? "var(--hm-green-700)" : "var(--text-muted)",
                        }}
                      >
                        <icons.IconDoor size={15} />
                      </span>
                      <span style={{ flex: 1, minWidth: 0 }}>
                        <span style={{ display: "block", ...ITEM_NAME_STYLE, fontWeight: selected ? 600 : 500 }}>
                          {unit.unitName}
                        </span>
                        <span style={{ display: "block", ...ITEM_DETAIL_STYLE }}>{unit.siteName}</span>
                      </span>
                      {selected && <icons.IconCheck size={15} style={{ color: "var(--hm-green-600)", flexShrink: 0 }} />}
                    </button>
                  );
                })}
              </div>
            </>
          )}

          {onAddSite !== undefined && (
            <>
              {/* Abgesetzt: Alles darüber wählt aus, was es gibt — dieser
                  Eintrag verlässt die Auswahl und öffnet die Verwaltung. */}
              <div
                role="separator"
                style={{ height: 1, background: "var(--border-subtle)", margin: "5px 4px" }}
              />
              <button
                type="button"
                role="menuitem"
                onMouseEnter={() => setHovered("add")}
                onMouseLeave={() => setHovered(null)}
                onClick={() => {
                  onAddSite();
                  setOpen(false);
                }}
                style={{
                  ...ITEM_STYLE,
                  /* Als einzige Zeile im Grün der Marke: Sie führt nicht zu einer
                     anderen Ansicht derselben Sache, sondern legt etwas an. */
                  color: "var(--hm-green-700)",
                  fontFamily: "var(--font-body)",
                  fontSize: 13,
                  fontWeight: 600,
                  background: hovered === "add" ? "var(--surface-sunken)" : "transparent",
                }}
              >
                <span style={{ ...ITEM_ICON_STYLE, background: "var(--hm-green-50)" }}>
                  <icons.IconPlus size={15} />
                </span>
                Anlage hinzufügen
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Die zweite Zeile: die Adresse, wie im Kit („Musterstraße 12, Köln").
 *
 * Sie ist im Vertrag vollständig `nullable` — eine Anlage kann angelegt werden,
 * ohne dass jemand die Adresse kennt. Fehlt sie, tritt die **Zeitzone** an ihre
 * Stelle: Sie ist ohnehin die Angabe, an der in diesem Produkt am meisten hängt
 * (jeder Tag beginnt an ihrer Ortsmitternacht), und sie ist immer vorhanden.
 * Eine leere Zeile stehen zu lassen, ließe die Karte kaputt aussehen.
 */
function secondLine(site: Site): string {
  if (site.status === "archived") return "archiviert";

  const address = site.address;
  if (address === null) return site.timezone;

  const street = [address.street, address.houseNumber].filter(Boolean).join(" ");
  const place = [address.postalCode, address.city].filter(Boolean).join(" ");
  const full = [street, place].filter((part) => part.length > 0).join(", ");

  return full.length > 0 ? full : site.timezone;
}

const SHELL_STYLE = {
  padding: "9px 10px",
  borderRadius: "var(--radius-md)",
  border: "1px solid var(--border-subtle)",
  background: "var(--surface-inset)",
} as const;

/** Eine Zeile, die nicht umbricht, sondern endet. */
const TRUNCATE = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const NAME_STYLE = {
  fontSize: 13.5,
  fontWeight: 600,
  color: "var(--text-strong)",
  ...TRUNCATE,
} as const;

const DETAIL_STYLE = {
  fontSize: 11.5,
  color: "var(--text-muted)",
  ...TRUNCATE,
} as const;

/** Im Menü eine halbe Stufe kleiner als im Kopf — so steht der Kopf voran. */
const ITEM_NAME_STYLE = {
  fontSize: 13,
  color: "var(--text-strong)",
  ...TRUNCATE,
} as const;

const ITEM_DETAIL_STYLE = {
  fontSize: 11,
  color: "var(--text-muted)",
  ...TRUNCATE,
} as const;

const HEADING_STYLE = {
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--text-faint)",
  padding: "6px 9px 4px",
} as const;

const ITEM_STYLE = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  padding: "8px 9px",
  border: "none",
  borderRadius: "var(--radius-sm)",
  cursor: "pointer",
  textAlign: "left",
  transition: "background .15s ease",
} as const;

const ITEM_ICON_STYLE = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 28,
  height: 28,
  flexShrink: 0,
  borderRadius: "var(--radius-sm)",
} as const;
